/**
 * Public telemetry API. Components import from here only.
 *
 * Everything is fire-and-forget and safe to call unconditionally — when tracking
 * is off or unauthenticated the calls are cheap no-ops, so callers never need a
 * guard.
 */

import { TELEMETRY_KPI_HOVER_MIN_MS } from "src/constants/telemetry";

import { EngagedTimer, idleGate } from "./idleGate";
import { ActivityProperties, ClientActivityName, telemetryQueue } from "./queue";

export { idleGate, EngagedTimer } from "./idleGate";
export { telemetryQueue } from "./queue";
export type { ActivityProperties, ClientActivityName } from "./queue";

/** Report a one-off activity. */
export const track = (
  name: ClientActivityName,
  detail: {
    dashboardCode?: string | null;
    widgetCode?: string | null;
    subjectId?: string | null;
    durationMs?: number;
    properties?: ActivityProperties;
  } = {},
): void => {
  telemetryQueue.push({
    name,
    dashboard_code: detail.dashboardCode ?? null,
    widget_code: detail.widgetCode ?? null,
    subject_id: detail.subjectId ?? null,
    duration_ms: detail.durationMs,
    properties: detail.properties,
  });
};

// --- KPI hover ------------------------------------------------------------
//
// Hovers are COALESCED per widget rather than one event per hover. A user
// glancing across a 15-widget dashboard would otherwise generate dozens of rows
// per visit and dominate the whole dataset; one row carrying total dwell plus an
// occurrence count answers "which KPIs get used" just as well.

interface HoverAggregate {
  dashboardCode: string | null;
  totalMs: number;
  engagedMs: number;
  count: number;
  /**
   * Real wall-clock instant the most recent hover in this aggregate ended.
   *
   * Without this, every row reported `occurred_at = now` at DRAIN time rather
   * than when the hover actually happened — and since `drainKpiHovers` runs on
   * a fixed timer, any hovers sitting completed-but-undrained since the last
   * tick all got stamped with the SAME instant, no matter how far apart they
   * really were. That is why a session could show eight different KPIs
   * "viewed" at the exact same millisecond.
   */
  lastEndedAt: number;
}

/**
 * A hover currently in progress.
 *
 * `dashboardCode` is captured at START rather than passed at end, so a hover can
 * be settled by something other than the pointer leaving (a page close, or the
 * visit ending) without that caller needing to know which dashboard it was on.
 *
 * `engaged` measures the same span through the idle gate. A cursor parked on a
 * widget while the user is away would otherwise accrue dwell indefinitely, so
 * both figures are stored and the report can choose — the same reasoning as
 * dashboard dwell keeping wall-clock and engaged side by side.
 */
interface HoverStart {
  at: number;
  dashboardCode: string | null;
  engaged: EngagedTimer;
}

const hoverStartedAt = new Map<string, HoverStart>();
const hoverTotals = new Map<string, HoverAggregate>();

export const beginKpiHover = (widgetCode: string, dashboardCode: string | null = null): void => {
  if (!widgetCode) return;
  // Re-entering without a matching leave (React remount, pointer re-entry): drop
  // the stale timer so its listener does not leak.
  hoverStartedAt.get(widgetCode)?.engaged.dispose();
  hoverStartedAt.set(widgetCode, {
    at: Date.now(),
    dashboardCode,
    engaged: new EngagedTimer(),
  });
};

export const endKpiHover = (widgetCode: string): void => {
  const start = hoverStartedAt.get(widgetCode);
  if (start === undefined) return;
  hoverStartedAt.delete(widgetCode);

  // Captured once, right where the hover truly ends — reused below as the
  // aggregate's `lastEndedAt` rather than letting the eventual drain/push
  // stamp its own, unrelated "now".
  const endedAt = Date.now();
  const dwell = endedAt - start.at;
  const engagedMs = start.engaged.read();
  start.engaged.dispose();

  // Below the threshold this is the pointer crossing the widget, not attention.
  if (dwell < TELEMETRY_KPI_HOVER_MIN_MS) return;

  const existing = hoverTotals.get(widgetCode);
  if (existing) {
    existing.totalMs += dwell;
    existing.engagedMs += engagedMs;
    existing.count += 1;
    existing.lastEndedAt = endedAt;
  } else {
    hoverTotals.set(widgetCode, {
      dashboardCode: start.dashboardCode,
      totalMs: dwell,
      engagedMs,
      count: 1,
      lastEndedAt: endedAt,
    });
  }
};

/**
 * Close out every hover still in progress, as if the pointer had left.
 *
 * Needed because `mouseleave` does not fire when the page is closing or the
 * dashboard unmounts — so without this, whatever the user was looking at when
 * they left is the one thing never recorded.
 */
export const settleKpiHovers = (): void => {
  [...hoverStartedAt.keys()].forEach((widgetCode) => endKpiHover(widgetCode));
};

/**
 * Move COMPLETED hovers into the queue.
 *
 * Called on every flush (~10s) so a long visit reports as it goes. Emitting
 * across several flushes is harmless: the rollup sums `occurrences` and duration.
 *
 * Critically this must NOT touch `hoverStartedAt`. It used to clear it, which
 * destroyed the start timestamp of any hover still in progress — so when the
 * pointer finally left, `endKpiHover` found nothing and discarded the hover
 * entirely. With flushes every 10 seconds, any hover straddling a flush boundary
 * vanished, leaving only the hovers that happened to fit between two flushes.
 * That is why KPI seconds looked arbitrary and under-counted.
 */
export const drainKpiHovers = (): void => {
  hoverTotals.forEach((agg, widgetCode) => {
    telemetryQueue.push({
      name: "kpi.hovered",
      dashboard_code: agg.dashboardCode,
      widget_code: widgetCode,
      duration_ms: agg.totalMs,
      engaged_ms: agg.engagedMs,
      occurrences: agg.count,
      // The real instant this hover (or the last of several coalesced ones)
      // ended — NOT when this drain happens to run. Draining is on a timer,
      // so without this every hover still undrained since the last tick would
      // otherwise be stamped with the exact same "now".
      occurredAt: new Date(agg.lastEndedAt).toISOString(),
    });
  });
  hoverTotals.clear();
};

// --- Dashboard visit ------------------------------------------------------

export interface DashboardVisit {
  /** Emit dashboard.dwell with wall-clock and engaged time, then release. */
  end: () => void;
  /**
   * Release WITHOUT reporting. Used only for a StrictMode/remount artifact, where
   * the visit lived for milliseconds and was immediately superseded by an identical
   * one. Suppressing by duration instead would also discard a genuine brief visit
   * that ended in a tab close — which is a real measurement, and one there is a
   * regression test for.
   */
  discard: () => void;
}

/**
 * The visit currently being timed, if any.
 *
 * Tracked at module level so sign-out can close it. A visit normally ends on
 * DashboardPage's unmount, but `useLogout` clears the access token BEFORE that
 * unmount happens — so the final dashboard's dwell was being queued with no
 * credential left to send it, and silently dropped. `endOpenVisit()` lets logout
 * close the visit while the token is still valid.
 */
let openVisit: DashboardVisit | null = null;

/** Close any in-progress dashboard visit. Safe to call when there is none. */
export const endOpenVisit = (): void => {
  openVisit?.end();
  openVisit = null;
};

/** Drop the open visit unreported. Only for the remount artifact — see `discard`. */
const discardOpenVisit = (): void => {
  openVisit?.discard();
  openVisit = null;
};

/**
 * Which way the user opened a dashboard.
 *
 * Viewing and editing are genuinely different activities on the same dashboard,
 * and the server cannot tell them apart — `GET /dashboards/{code}` is called by
 * both. They are separate ROUTES on the client though (`/dashboards/:code` vs
 * `/builder/:code`), so each page declares its own mode and the two get distinct
 * activity names. Reporting can then answer "who reads this dashboard" and "who
 * maintains it" separately, instead of conflating them into one Opens count.
 */
const VISIT_MODES = {
  view: { opened: "dashboard.viewed", dwell: "dashboard.dwell" },
  build: { opened: "builder.opened", dwell: "builder.dwell" },
} as const;

type VisitMode = keyof typeof VISIT_MODES;

/**
 * Begin timing a visit to one dashboard, in the given mode.
 *
 * Both figures are reported: `duration_ms` is wall-clock, `engaged_ms` applies
 * the idle gate. Keeping both means the gate can be re-tuned later without
 * losing an ungated baseline — and it lets a report distinguish "tab left open
 * for an hour" from "an hour of use".
 *
 * There is only ever ONE open visit: you are either viewing a dashboard or
 * editing one, never both, so moving between the two closes the previous visit.
 */
/**
 * The last visit opened, for remount detection.
 *
 * React StrictMode double-invokes effects in development: mount → unmount → mount.
 * The `end()` side was already guarded, but the OPEN side was not, so every visit
 * emitted its "opened" event twice — two identical blocks in the timeline seconds
 * apart, and an Opens count of 2 for one visit. A genuine remount (routing away and
 * straight back) would do the same in production.
 */
let lastOpen: { code: string; mode: VisitMode; at: number } | null = null;

/** Below this, a re-open of the same dashboard is a remount, not a second visit. */
const REMOUNT_WINDOW_MS = 2_000;

const startVisit = (dashboardCode: string, mode: VisitMode): DashboardVisit => {
  const names = VISIT_MODES[mode];

  // Decided BEFORE closing the previous visit, because the answer determines
  // whether that visit is reported or thrown away.
  const isRemount =
    lastOpen !== null &&
    lastOpen.code === dashboardCode &&
    lastOpen.mode === mode &&
    Date.now() - lastOpen.at < REMOUNT_WINDOW_MS;

  // Navigating between dashboards, or from a dashboard into the builder, must not
  // leave the previous visit un-reported — but a remount's throwaway first visit
  // must not be reported at all.
  if (isRemount) discardOpenVisit();
  else endOpenVisit();

  // The authoritative "this dashboard was opened" signal. It lives here rather
  // than on the server's config route because that route is also called by the
  // builder, which counted every edit as a view. This fires exactly once per real
  // visit, so the Opens column now means what it says.
  if (!isRemount) {
    telemetryQueue.push({ name: names.opened, dashboard_code: dashboardCode });
  }
  lastOpen = { code: dashboardCode, mode, at: Date.now() };

  const openedAt = Date.now();
  const engaged = new EngagedTimer();
  let ended = false;

  const release = () => {
    ended = true;
    if (openVisit === visit) openVisit = null;
    engaged.dispose();
  };

  const visit: DashboardVisit = {
    discard: () => {
      if (ended) return;
      release();
    },
    end: () => {
      if (ended) return; // React can unmount twice in StrictMode
      const engagedMs = engaged.read(); // read before release() disposes the timer
      release();
      // Settle first: the widget the user was hovering when they navigated away
      // gets no `mouseleave`, so without this it is the one hover never recorded.
      settleKpiHovers();
      drainKpiHovers();

      telemetryQueue.push({
        name: names.dwell,
        dashboard_code: dashboardCode,
        duration_ms: Date.now() - openedAt,
        engaged_ms: engagedMs,
      });
    },
  };

  openVisit = visit;
  return visit;
};

/** Time spent READING a dashboard. Emits dashboard.viewed / dashboard.dwell. */
export const startDashboardVisit = (dashboardCode: string): DashboardVisit =>
  startVisit(dashboardCode, "view");

/**
 * Time spent EDITING a dashboard in the builder. Emits builder.opened /
 * builder.dwell.
 *
 * Separate from a view because they answer different questions, and because the
 * builder is a sibling route group — `/builder/:code` rather than a child of the
 * dashboard route — so nothing else would attribute this time to a dashboard.
 */
export const startBuilderVisit = (dashboardCode: string): DashboardVisit =>
  startVisit(dashboardCode, "build");
